import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pillarArticles } from '@/mocks/pillarArticles';
import { applySeoMeta, getSiteUrl, initialSeo, setRobots } from '@/lib/seo';

type Lang = 'en' | 'pt' | 'de';

function normalizeLang(lng: string | undefined): Lang {
  const base = (lng || 'pt').split('-')[0];
  return base === 'en' || base === 'de' ? base : 'pt';
}

export default function SeoManager() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = normalizeLang(i18n.language);
    const siteUrl = getSiteUrl();

    const isHome =
      pathname === '/' || pathname === '/pt' || pathname === '/en' || pathname === '/de';

    if (isHome) {
      applySeoMeta({
        title: initialSeo.title,
        description: initialSeo.description,
        keywords: initialSeo.keywords,
        canonical: initialSeo.canonical,
        ogType: initialSeo.ogType,
        ogTitle: initialSeo.ogTitle,
        ogDescription: initialSeo.ogDescription,
        ogUrl: initialSeo.ogUrl,
        twitterTitle: initialSeo.twitterTitle,
        twitterDescription: initialSeo.twitterDescription,
      });
      setRobots('index, follow');
      return;
    }

    const articleMatch = pathname.match(/^\/(en|pt|de)\/cssbuy-spreadsheet-guide$/);
    if (articleMatch) {
      const title = i18n.t('meta.title', { ns: 'article' });
      const description = i18n.t('meta.description', { ns: 'article' });
      const canonical = `${siteUrl}${pathname}`;
      const keywords =
        'CSSBuy Spreadsheet, CSSBuy Guide, CSSBuy Product Research, CSSBuy QC, CSSBuy Shipping, CSSBuy Comparison';
      applySeoMeta({
        title,
        description,
        keywords,
        canonical,
        ogType: 'article',
        ogTitle: title,
        ogDescription: description,
        ogUrl: canonical,
        twitterTitle: title,
        twitterDescription: description,
      });
      setRobots('index, follow');
      return;
    }

    const pillarMatch = pathname.match(/^\/(en|pt|de)\/guide\/([^/]+)$/);
    if (pillarMatch) {
      const slug = pillarMatch[2];
      const article = pillarArticles.find((a) => a.slug === slug);
      if (article) {
        const title = article.title[lang];
        const description = article.summary[lang];
        const canonical = `${siteUrl}${pathname}`;
        const keywords = `CSSBuy Spreadsheet, CSSBuy, ${title}, CSSBuy Guide`;
        applySeoMeta({
          title,
          description,
          keywords,
          canonical,
          ogType: 'article',
          ogTitle: title,
          ogDescription: description,
          ogUrl: canonical,
          twitterTitle: title,
          twitterDescription: description,
        });
        setRobots('index, follow');
        return;
      }
    }

    setRobots('noindex, follow');
  }, [pathname, i18n.language]);

  return null;
}